import { bool, func } from 'prop-types';
import React, { useState } from 'react';
import { Col, FormGroup, Input, Label, Row } from 'reactstrap';

import Association from '../../associations/Association';
import {
  adOpsDelayedStartReasonsOptions,
  boolOptions,
  pcDelayedStartReasonsOptions,
  underPerformanceReasonsOptions,
} from '../../campaigns/constants';
import { commercialIncentivesOptions } from '../../constants';
import CurrencyInput from '../../generic/CurrencyInput';
import DateRange from '../../generic/DateRange';
import MultiSelect from '../../generic/MultiSelect';
import SingleSelect from '../../generic/SingleSelect';
import TimestampInfo from '../../generic/TimestampInfo';
import { getTooltipLabelWithText } from '../../generic/Tooltip';
import { isAdOps, isPC } from '../../layout/Variant/Variant';
import { billableBudgetShape, campaignShape } from '../../prop-types';
import BillableBudget from '../../quote-editor/BillableBudget';
import FlexibilityTerms from '../../quote-editor/FlexibilityTerms';
import { DISCRETIONARY_MEDIA } from '../constants';
import MandatoryDelayReason from '../MandatoryDelayReason';
import HELP_TEXTS from '../utils/help-texts';
import AutomateBgColorsConfirmation from './AutomateBgColorsConfirmation';

function CampaignForm({
  model,
  onChange,
  billableBudgets,
  isEditing,
  isDelayReasonVisible,
  changeDelayReasonVisibility,
  isPushedToCF,
  hasHPTOSelected,
}) {
  const {
    quote,
    opportunity,
    ownerId,
    collaboratorIds,
    brandIds,
    name,
    startDatePlan,
    endDatePlan,
    startDateIs,
    endDateIs,
    adOpsDelayedStartReasons,
    adOpsDelayedStartReasonsUpdatedAt,
    pcDelayedStartReasons,
    pcDelayedStartReasonsUpdatedAt,
    delayedStartComment,
    targetKpi,
    targetKpiValue,
    underPerformanceReasons,
    hasScalableBudget,
    underPerformanceReasonsUpdatedAt,
    useRecommendedColors,
    commercialIncentive,
    additionalDetails,
    hasCFCampaignLevelSteering,
    hasCCPCampaignLevelSteering,
    merchantIds,
  } = model;

  const inputProps = {
    onChange,
    readOnly: !onChange || !isEditing,
    disabled: !onChange || !isEditing,
  };
  const KpiComponent = targetKpi === 'Min. ROAS' ? Input : CurrencyInput;

  const kpiProps =
    targetKpi === 'Min. ROAS'
      ? {
          type: 'number',
          step: '0.01',
        }
      : null;

  const TooltipLabel = getTooltipLabelWithText(HELP_TEXTS);

  const [isAutomateBdColorsModalOpen, setIsAutomateBdColorsModalOpen] =
    useState(false);

  const toggleAutomateBgColorsModal = () =>
    setIsAutomateBdColorsModalOpen(!isAutomateBdColorsModalOpen);

  const onClickAutomateBgColorsModal = () => {
    onChange({
      target: { name: 'useRecommendedColors', value: false },
    });
    toggleAutomateBgColorsModal();
  };

  const handleAutomateBgColorsChange = () => {
    if (useRecommendedColors) {
      toggleAutomateBgColorsModal();
    } else {
      onChange({
        target: { name: 'useRecommendedColors', value: !useRecommendedColors },
      });
    }
  };

  const { accountSfId } = opportunity;

  return (
    <Row>
      <Col sm={6} className="mb-4">
        <FormGroup row>
          <Label sm={4} for="ca-name" className="required">
            Name
          </Label>
          <Col sm={8}>
            <Input
              id="ca-name"
              name="name"
              type="text"
              value={name}
              {...inputProps}
            />
          </Col>
        </FormGroup>
        <FormGroup row>
          <Label sm={4} for="ca-accountSfId" className="required">
            Account ID
          </Label>
          <Col sm={8}>
            <Input
              id="ca-accountSfId"
              name="accountSfId"
              type="text"
              value={accountSfId}
              {...inputProps}
              readOnly
            />
          </Col>
        </FormGroup>
        <FormGroup row>
          <Label sm={4} for="ca-opportunityId">
            Opportunity
          </Label>
          <Col sm={8}>
            <Association
              id="ca-opportunityId"
              name="opportunityId"
              value={opportunity.id}
              type="opportunity"
              showIcon
              readOnly
            />
          </Col>
        </FormGroup>
        <FormGroup row>
          <Label sm={4} for="ca-quoteId">
            Quote
          </Label>
          <Col sm={8}>
            <Association
              id="ca-quoteId"
              name="quoteId"
              value={quote.id}
              type="quote"
              showIcon
              readOnly
            />
          </Col>
        </FormGroup>
        <FormGroup row>
          <Label sm={4} for="ca-brandIds">
            Brands
          </Label>
          <Col sm={8}>
            <Association
              id="ca-brandIds"
              name="brandIds"
              value={brandIds}
              type="brand"
              isMulti
              showIcon
              verbose
              readOnly
              separator=","
            />
          </Col>
        </FormGroup>
        <FormGroup row>
          <Label sm={4} for="ca-merchantIds">
            Merchants
          </Label>
          <Col sm={8}>
            <Association
              id="ca-merchantIds"
              name="merchantIds"
              value={merchantIds}
              type="zmsamMerchant"
              {...inputProps}
              isMulti
              readOnly
            />
          </Col>
        </FormGroup>
        <FormGroup row>
          <Label sm={4} for="ca-targetKpiValue" className="mt-1">
            {targetKpi}
          </Label>
          <Col sm={4}>
            <KpiComponent
              id="ca-targetKpiValue"
              name="targetKpiValue"
              value={targetKpiValue || 0}
              {...kpiProps}
              readOnly
            />
          </Col>
        </FormGroup>
        <FormGroup row>
          <Label sm={4} for="ca-runtimePlan-startDatePlan">
            Planned Runtime
          </Label>
          <Col sm={8}>
            <DateRange
              id="ca-runtimePlan"
              startName="startDatePlan"
              endName="endDatePlan"
              startDate={startDatePlan}
              endDate={endDatePlan}
              readOnly
              small
            />
          </Col>
        </FormGroup>
        <FormGroup row>
          <Label sm={4} for="ca-runtimeIs-startDateIs">
            Actual Runtime
          </Label>
          <Col sm={8}>
            <DateRange
              id="ca-runtimeIs"
              startName="startDateIs"
              startDate={startDateIs}
              endName="endDateIs"
              endDate={endDateIs}
              readOnly
              small
            />
          </Col>
        </FormGroup>
        <FormGroup row>
          <Label sm={4} for="ca-commercialIncentive">
            Commercial Incentive
          </Label>
          <Col sm={8}>
            <SingleSelect
              options={commercialIncentivesOptions}
              id="ca-commercialIncentive"
              name="commercialIncentive"
              value={commercialIncentive}
              {...inputProps}
              readOnly
            />
          </Col>
        </FormGroup>
        {commercialIncentive === DISCRETIONARY_MEDIA && (
          <FormGroup row>
            <TooltipLabel
              helpKey="additionalDetails"
              text="Additional Details"
              section="readOnlyFields"
              colWidth={4}
              resource="ca"
            />
            <Col sm={8}>
              <Input
                id="ca-additionalDetails"
                name="additionalDetails"
                value={additionalDetails}
                type="textarea"
                {...inputProps}
                readOnly
              />
            </Col>
          </FormGroup>
        )}
        <FormGroup row>
          <TooltipLabel
            helpKey="useRecommendedColors"
            text="Automate Background Colors"
            colWidth={4}
            section="header"
            resource="ca"
          />
          <Col sm={8}>
            <Input
              type="checkbox"
              id="ca-useRecommendedColors"
              name="useRecommendedColors"
              checked={useRecommendedColors}
              className="form-control form-checkbox"
              {...inputProps}
              disabled={isPushedToCF || hasHPTOSelected}
              onChange={handleAutomateBgColorsChange}
            />
            <AutomateBgColorsConfirmation
              toggle={isAutomateBdColorsModalOpen}
              setToggle={toggleAutomateBgColorsModal}
              onClick={onClickAutomateBgColorsModal}
            />
          </Col>
        </FormGroup>
        <FormGroup row>
          <TooltipLabel
            helpKey="hasCFCampaignLevelSteering"
            text="CF Campaign Level Steering"
            colWidth={4}
            section="header"
            resource="ca"
          />
          <Col sm={8}>
            <Input
              type="checkbox"
              id="ca-hasCFCampaignLevelSteering"
              name="hasCFCampaignLevelSteering"
              checked={hasCFCampaignLevelSteering}
              className="form-control form-checkbox"
              disabled
            />
          </Col>
        </FormGroup>
        <FormGroup row>
          <TooltipLabel
            helpKey="hasCCPCampaignLevelSteering"
            text="CCP Campaign Level Steering"
            colWidth={4}
            section="header"
            resource="ca"
          />
          <Col sm={8}>
            <Input
              type="checkbox"
              id="ca-hasCCPCampaignLevelSteering"
              name="hasCCPCampaignLevelSteering"
              checked={hasCCPCampaignLevelSteering}
              className="form-control form-checkbox"
              disabled
            />
          </Col>
        </FormGroup>
      </Col>

      <Col sm={6} className="mb-4">
        <FormGroup row>
          <TooltipLabel
            helpKey="owner"
            text="Owner"
            colWidth={4}
            section="header"
            resource="ca"
          />
          <Col sm={8}>
            <Association
              id="ca-ownerId"
              name="ownerId"
              value={ownerId}
              type="user"
              {...inputProps}
              readOnly
            />
          </Col>
        </FormGroup>
        <FormGroup row>
          <TooltipLabel
            text="Collaborators"
            colWidth={4}
            helpKey="collaboratorIds"
            section="readOnlyFields"
            resource="ca"
          />
          <Col sm={8}>
            <Association
              id="ca-collaboratorIds"
              name="collaboratorIds"
              value={collaboratorIds}
              type="user"
              isMulti
              showTeam
              readOnly
            />
          </Col>
        </FormGroup>
        <FormGroup
          row
          className={underPerformanceReasonsUpdatedAt ? 'mb-0' : null}
        >
          <Label sm={4} for="ca-underPerformanceReasons">
            Underperforming Reasons
          </Label>
          <Col sm={8}>
            <MultiSelect
              options={underPerformanceReasonsOptions}
              id="ca-underPerformanceReasons"
              name="underPerformanceReasons"
              value={underPerformanceReasons}
              {...inputProps}
            />
          </Col>
        </FormGroup>
        {underPerformanceReasonsUpdatedAt && (
          <FormGroup row>
            <Col sm={4} />
            <Col sm={8}>
              <div className="small">
                <TimestampInfo
                  timestamp={underPerformanceReasonsUpdatedAt}
                  actionName="lastModified"
                />
              </div>
            </Col>
          </FormGroup>
        )}
        <FormGroup row>
          <Label sm={4} for="ca-hasScalableBudget">
            Scalable Budget
          </Label>
          <Col sm={8}>
            <SingleSelect
              options={boolOptions}
              id="ca-hasScalableBudget"
              name="hasScalableBudget"
              value={hasScalableBudget}
              {...inputProps}
            />
          </Col>
        </FormGroup>
        <FlexibilityTerms
          model={model}
          section="ca"
          labelwidth={4}
          contentwidth={8}
          {...inputProps}
          readOnly
        />
        <FormGroup
          row
          className={adOpsDelayedStartReasonsUpdatedAt ? 'mb-0' : null}
        >
          <TooltipLabel
            helpKey="adOpsDelayedStartReasons"
            text="Campaign Delay Reasons"
            colWidth={4}
            section="header"
            resource="ca"
          />
          <Col sm={8}>
            <MultiSelect
              options={adOpsDelayedStartReasonsOptions}
              id="ca-adOpsDelayedStartReasons"
              name="adOpsDelayedStartReasons"
              value={adOpsDelayedStartReasons}
              {...inputProps}
              readOnly={isPC()}
            />
          </Col>
        </FormGroup>
        {adOpsDelayedStartReasonsUpdatedAt && (
          <FormGroup row>
            <Col sm={4} />
            <Col sm={8}>
              <div className="small">
                <TimestampInfo
                  timestamp={adOpsDelayedStartReasonsUpdatedAt}
                  actionName="lastModified"
                />
              </div>
            </Col>
          </FormGroup>
        )}
        <FormGroup
          row
          className={pcDelayedStartReasonsUpdatedAt ? 'mb-0' : null}
        >
          <TooltipLabel
            helpKey="pcDelayedStartReasons"
            text="Briefing Delay Reasons"
            colWidth={4}
            section="header"
            resource="ca"
          />
          <Col sm={8}>
            <MultiSelect
              options={pcDelayedStartReasonsOptions}
              id="ca-pcDelayedStartReasons"
              name="pcDelayedStartReasons"
              value={pcDelayedStartReasons}
              {...inputProps}
              readOnly={isAdOps()}
            />
          </Col>
        </FormGroup>
        <MandatoryDelayReason
          pcDelayedStartReasons={pcDelayedStartReasons}
          onChange={onChange}
          isDelayReasonVisible={isDelayReasonVisible}
          changeDelayReasonVisibility={changeDelayReasonVisibility}
        />
        {pcDelayedStartReasonsUpdatedAt && (
          <FormGroup row>
            <Col sm={4} />
            <Col sm={8}>
              <div className="small">
                <TimestampInfo
                  timestamp={pcDelayedStartReasonsUpdatedAt}
                  actionName="lastModified"
                />
              </div>
            </Col>
          </FormGroup>
        )}

        <FormGroup row>
          <Label sm={4} for="ca-delayedStartComment">
            Delay Comment
          </Label>
          <Col sm={8}>
            <Input
              id="ca-delayedStartComment"
              name="delayedStartComment"
              type="textarea"
              value={delayedStartComment}
              {...inputProps}
            />
          </Col>
        </FormGroup>
        <BillableBudget billableBudgets={billableBudgets} />
      </Col>
    </Row>
  );
}

CampaignForm.propTypes = {
  model: campaignShape.isRequired,
  onChange: func,
  isEditing: bool,
  billableBudgets: billableBudgetShape.isRequired,
  isDelayReasonVisible: bool,
  changeDelayReasonVisibility: func,
  isPushedToCF: bool.isRequired,
  hasHPTOSelected: bool.isRequired,
};

CampaignForm.defaultProps = {
  onChange: undefined,
  isEditing: true,
  isDelayReasonVisible: false,
  changeDelayReasonVisibility: undefined,
};

export default CampaignForm;
